import jsPDF from 'jspdf';
import type { ScriptBlockData } from '../components/story/ScriptBlock';
import type { TitlePageData } from '../components/story/TitlePage';
import { SCRIPT_PAGE, hasUnsupportedPDFText, layoutPrintDocument, layoutScreenplay, type ScriptLayout } from './scriptLayout';
import {printLineRuns,scriptFontSize,courierTextWidth} from './scriptRichText';
import {revisionAppearance,revisionPrintPage,validateRevisionReferences,type ScriptRevisions} from './scriptRevisions';

/** No independent wrapping or Y bookkeeping: consume the exact preview layout. */
export function createScriptPDF(layout:ScriptLayout, titlePage:TitlePageData, blocks:readonly ScriptBlockData[] = [],revisions?:ScriptRevisions):jsPDF {
    validateRevisionReferences(blocks,revisions);
    if (layout.issues.length) throw new Error(layout.issues[0]);
    const pages = layoutPrintDocument(layout,titlePage);
    if (pages.some(page => page.lines.some(line => hasUnsupportedPDFText(line.text)))) {
        throw new Error('PDF export cannot render some title-page characters with the current font. A font with broader character support is needed.');
    }
    const doc = new jsPDF({orientation:'portrait',unit:'pt',format:'letter'});
    doc.setFontSize(SCRIPT_PAGE.fontSize);
    doc.setLineHeightFactor(1);
    const byId = new Map(blocks.map(block => [block.id, block]));
    pages.forEach((page,index) => {
        if (index) doc.addPage();
        const revisionPage=revisionPrintPage(page,blocks,revisions);
        if([...revisionPage.marks,...revisionPage.header].some(m=>hasUnsupportedPDFText(m.text)))throw new Error('This revision mark or label needs a font with broader character support.');
        if(revisionPage.pageColor){doc.setFillColor(revisionPage.pageColor);doc.rect(0,0,SCRIPT_PAGE.width,SCRIPT_PAGE.height,'F');}
        page.lines.forEach(line => {
            doc.setFontSize(line.fontSize??SCRIPT_PAGE.fontSize);
            doc.setTextColor('#000000');doc.setDrawColor('#000000');
            if ((!byId.get(line.blockId)?.richText && !byId.get(line.blockId)?.dualDialogue) || line.kind !== 'content') {
                doc.setFont('Courier',line.type === 'parenthetical' ? 'italic' : 'normal');
                doc.text(line.text,line.x,line.y,{align:line.align});
                return;
            }
            const runs = printLineRuns(line, byId.get(line.blockId));
            const width = runs.reduce((sum,run)=>sum+courierTextWidth(run.text,scriptFontSize(run.attributes.Size,line.fontSize??12)),0);
            let x = line.x - (line.align === 'right' ? width : line.align === 'center' ? width / 2 : 0);
            for (const run of runs) {
                const appearance=revisionAppearance(run,revisions,revisionPage.setId),styles = appearance.styles, bold = styles.includes('Bold');
                doc.setTextColor(appearance.color??'#000000');doc.setDrawColor(appearance.color??'#000000');
                const italic = styles.includes('Italic') || line.type === 'parenthetical';
                const size=scriptFontSize(run.attributes.Size,line.fontSize??12);doc.setFontSize(size);
                doc.setFont('Courier', bold ? italic ? 'bolditalic' : 'bold' : italic ? 'italic' : 'normal');
                doc.text(run.text, x, line.y);
                const runWidth = courierTextWidth(run.text,size);
                if (styles.includes('Underline')) {doc.setLineWidth(0.5); doc.line(x, line.y + 1.5, x + runWidth, line.y + 1.5);}
                x += runWidth;
            }
        });
        doc.setFontSize(SCRIPT_PAGE.fontSize);
        revisionPage.marks.forEach(mark=>{doc.setFont('Courier','normal');doc.setTextColor(mark.color);doc.text(mark.text,mark.x,mark.y);});
        revisionPage.header.forEach(h=>{doc.setFont('Courier','normal');doc.setTextColor('#000000');doc.text(h.text,h.x,h.y);});
    });
    return doc;
}

export function exportToPDF(
    blocks:readonly ScriptBlockData[], titlePage:TitlePageData,
    layout = layoutScreenplay(blocks),
    revisions?:ScriptRevisions,
):void {
    const name = (titlePage.title || 'untitled_screenplay').replace(/[^a-z0-9]/gi,'_').toLowerCase();
    createScriptPDF(layout,titlePage,blocks,revisions).save(`${name}.pdf`);
}
